import React, { Fragment, memo } from 'react';
import { styled, css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { APP_NAV_CONTAINER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component';
import { LoadableLazy, LoadableAfterPaint } from '@confluence/loadable';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { fg } from '@confluence/feature-gating';

import { CommonSpaceNavigationContent } from './CommonSpaceNavigationContent';
import { SpaceHeader } from './SpaceHeader';
import { ContainerSkeleton } from './ContainerSkeleton';
import { SpaceLinks } from './SpaceLinks';
import { SPACE_APPS_METRIC } from './perf.config';

const ErrorView = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ErrorView" */ './ErrorView')).ErrorView,
});

const SpaceApps = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceApps" */ './SpaceApps')).SpaceApps,
});

const spaceLinksStyles = css({
	margin: token('space.0'),
	padding: token('space.0'),
	listStyle: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:not(:first-of-type)': {
		marginTop: token('space.0'),
	},
});

const SidebarContent = memo(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div({
		paddingBottom: token('space.0'),
		position: 'relative',
		marginTop: 'auto',
	}),
);

const separatorStyles = css({
	marginTop: token('space.150'),
	marginInline: token('space.negative.150'),
	border: '0',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

export const SpaceNavigationContent = ({ navigationProps, errorProps, spaceDetails }) => {
	const {
		loading,
		hasSpace,
		space,
		spaceKey,
		homepageId,
		main,
		quick,
		isSpaceAdmin,
		isBlogNavigation,
		blogsHidden,
		pageTreeFinishedLoading,
		onDragDropSuccess,
		showShortcutsSection,
		shouldRenderSpaceApps,
		allAppLinks,
		allVisibleAppLinks,
		loadingForgeApps,
		onContentTreeLoadComplete,
	} = navigationProps;

	const { unhandledError, hasRestrictedError, hasNotFoundError, anonymousAccessQueryError } =
		errorProps;

	const isNav4Enabled = useIsNav4Enabled();

	if (
		unhandledError ||
		hasRestrictedError ||
		hasNotFoundError ||
		anonymousAccessQueryError ||
		(!loading && !hasSpace)
	) {
		return (
			<ErrorView
				unhandledError={unhandledError || anonymousAccessQueryError}
				hasRestrictedError={hasRestrictedError}
				hasNotFoundError={hasNotFoundError}
			/>
		);
	}
	return (
		<>
			<SpaceHeader {...spaceDetails} />
			<SidebarContent data-vc="space-navigation-sidebar-content">
				{!loading ? <ExperienceSuccess name={APP_NAV_CONTAINER_EXPERIENCE} /> : <Fragment />}
				<CommonSpaceNavigationContent
					showShortcutsSection={showShortcutsSection}
					space={space}
					spaceKey={spaceKey}
					quick={quick}
					homepageId={homepageId}
					loading={loading}
					isSpaceAdmin={isSpaceAdmin}
					isBlogNavigation={isBlogNavigation}
					blogsHidden={blogsHidden}
					pageTreeFinishedLoading={pageTreeFinishedLoading}
					onDragDropSuccess={onDragDropSuccess}
					onContentTreeLoadComplete={onContentTreeLoadComplete}
				/>

				<ul css={spaceLinksStyles} data-testid="space-links">
					{loading ? <ContainerSkeleton header={false} /> : <SpaceLinks links={main} />}
				</ul>

				{!loading && !loadingForgeApps && shouldRenderSpaceApps ? (
					<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
						<PageSegmentLoadStart metric={SPACE_APPS_METRIC} />
						<SpaceApps
							spaceKey={spaceKey}
							isSpaceAdmin={isSpaceAdmin}
							allAppLinks={allAppLinks}
							allVisibleAppLinks={allVisibleAppLinks}
						/>
					</ErrorBoundary>
				) : (
					<Fragment />
				)}
			</SidebarContent>
			{isNav4Enabled && !loading && fg('nav4_global_links_confluence') && (
				<hr role="presentation" css={separatorStyles} id="space-header-top-divider" />
			)}
		</>
	);
};
